import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { AgreementsService } from 'src/app/pages/agreements/services/agreements.service';
import { RateCard } from 'src/app/pages/agreements/types/agreement-rate-card.model';
import { CalculatedRateData } from 'src/app/pages/agreements/types/calculated-rate-data';
import { EventTrackingEnum } from 'src/app/shared/enums/event-tracking.enum';
import { Rate } from 'src/app/shared/models/gql/rate.model';
import { PriceLimitAlertData } from 'src/app/shared/models/price-limit-alert-data.model';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { PriceLimitExceededServices } from 'src/app/shared/types/price-limit-exceeded-data';

@Component({
  selector: 'app-ndis-price-limit-alert',
  templateUrl: './ndis-price-limit-alert.component.html',
  styleUrls: ['./ndis-price-limit-alert.component.scss'],
})
export class NdisPriceLimitAlertComponent implements OnChanges {
  // Expose EventTrackingEnum for use in template
  readonly EventTrackingEnum = EventTrackingEnum;

  // Data for segment events
  @Input() priceLimitAlertData: PriceLimitAlertData;

  // Rate data needed to fill out banner content
  @Input() rateData: Rate | RateCard;

  @Input() legacyAgreement = false;

  // Flagger to indicate to allow moving services to another service group.
  @Input() canMoveServiceToNewGroup = false;

  // EventEmitter for clicking rate editor
  @Output() editRate = new EventEmitter<null>();

  @Output() editService = new EventEmitter<null>();

  @Output() requestService = new EventEmitter<null>();

  @Output() moveService = new EventEmitter<number>();

  calculatedRate: CalculatedRateData;

  exceededCapRates: PriceLimitExceededServices;

  hasAssistanceWithPersonalDomesticActivitiesService: boolean;

  ASSISTANCE_WITH_PERSONAL_DOMESTIC_ACTIVITIES_SERVICE_ID = 2;

  constructor(
    private utilityService: UtilityService,
    private agreementsService: AgreementsService,
  ) {}

  ngOnChanges(): void {
    this.setTemplateVariables();
  }

  /**
   * Calls the segment service to track or page price limit alert events, auto appends required properties.
   *
   * @param event - The segment event.
   */
  triggerSegmentEvent(event: EventTrackingEnum): void {
    if (!this.priceLimitAlertData) {
      return;
    }

    const priceLimitAlertType = `${this.calculatedRate.priceLimitAlertType} ${this.calculatedRate.priceLimitAlertType || ''}`;

    const properties = {
      priceLimitAlertType:
        priceLimitAlertType === 'Weekday between 6am - 8pm' ? 'Weekday Daytime' : priceLimitAlertType,
      ...this.priceLimitAlertData,
      rate: this.rateData,
    };

    this.agreementsService.agreementSegmentCall(event, properties, true);
  }

  /**
   * Opens the "Why there are price limits" link.
   */
  openWhyThereArePriceLimitsLink(): void {
    this.utilityService.openExternalLink(
      'https://mable.com.au/help_centre/ndis-price-limits-what-all-disability-support-workers-should-know/',
    );
  }

  /**
   * Opens the "View NDIS price limits" link.
   */
  openViewNdisPriceLimits(): void {
    this.utilityService.openExternalLink('https://www.ndis.gov.au/providers/pricing-arrangements');
  }

  /**
   * Triggered on click of the edit Rate button. Emits the event to the parent component.
   */
  onEditClick(): void {
    this.editRate.emit(null);
  }

  onEditServicesClick(): void {
    this.editService.emit();
  }

  onRequestServiceClick(): void {
    this.requestService.emit();
  }

  onMoveServiceClick(serviceId: number): void {
    this.moveService.emit(serviceId);
  }

  get canUseExceededCapRates(): boolean {
    if (this.rateData && this.isRateDataOfTypeRateCard(this.rateData)) {
      return Boolean(this.rateData.exceededCapRates?.length);
    }

    return false;
  }

  private setTemplateVariables(): void {
    // the template variables all come from the calculatedRate object inside a structured rate;
    if (this.isRateDataOfTypeRateCard(this.rateData)) {
      if (this.canUseExceededCapRates) {
        this.exceededCapRates = this.rateData.exceededCapRates;
        this.hasAssistanceWithPersonalDomesticActivitiesService = this.exceededCapRates.some(
          (service) => service.serviceId === this.ASSISTANCE_WITH_PERSONAL_DOMESTIC_ACTIVITIES_SERVICE_ID,
        );
      } else {
        this.calculatedRate = this.rateData.calculatedRate;
      }
    }

    if (this.priceLimitAlertData) {
      this.legacyAgreement = this.priceLimitAlertData.legacyAgreement;
    }
  }

  private isRateDataOfTypeRateCard(rateData: Rate | RateCard): rateData is RateCard {
    // calculatedRate, timeBlock and exceededCapRates are RateCard ONLY properties
    return 'calculatedRate' in rateData || 'timeBlock' in rateData || 'exceededCapRates' in rateData;
  }
}
